/**
 * TokenShopService — "Pay with Token" purchase flow.
 *
 * Handles:
 * - Loading token_shop_items from Supabase
 * - Calculating token prices from Pyth feeds (with manual rate fallback)
 * - End-to-end purchase flow: select -> confirm -> sign in wallet -> verify on-chain -> credit
 *
 * Key design: Team wallet address is NEVER exposed to the client.
 * The token-purchase-intent Edge Function constructs the full unsigned SPL transfer transaction
 * server-side with the team wallet embedded in the serialized bytes. The client only signs.
 */

import { getSupabase } from "../auth/supabaseClient";
import { solanaService } from "./solanaService";
import { fetchTokenUsdPrice } from "./pricingService";
import { mapWalletError } from "./walletErrorMapper";
import { Transaction } from "@solana/web3.js";
import type { WhitelistedToken } from "./solanaTypes";

// --- Cross-tab purchase lock (#143) ---
const PURCHASE_LOCK_KEY = "bb_purchase_lock";
const PURCHASE_LOCK_TTL_MS = 60_000; // 60 seconds

function acquirePurchaseLock(): boolean {
  try {
    const existing = localStorage.getItem(PURCHASE_LOCK_KEY);
    if (existing) {
      const ts = Number(existing);
      if (Number.isFinite(ts) && Date.now() - ts < PURCHASE_LOCK_TTL_MS) {
        return false; // another tab holds the lock
      }
    }
    localStorage.setItem(PURCHASE_LOCK_KEY, String(Date.now()));
    return true;
  } catch {
    return true; // localStorage unavailable — fall through to in-memory guard
  }
}

function releasePurchaseLock(): void {
  try {
    localStorage.removeItem(PURCHASE_LOCK_KEY);
  } catch {
    // ignore
  }
}

export interface ShopItem {
  id: string;
  itemType: "gems" | "skin" | "exclusive";
  itemId: string;
  displayName: string;
  usdPrice: number;
  gemAmount: number | null;
  sortOrder: number;
}

export interface TokenShopState {
  items: ShopItem[];
  selectedItem: ShopItem | null;
  selectedToken: WhitelistedToken | null;
  tokenPrice: number | null;
  purchaseState: "idle" | "confirming" | "signing" | "verifying" | "success" | "error" | "processing";
  purchaseError: string | null;
  lastPurchaseId: string | null;
  /** Number of verify poll attempts made during the current purchase. Reset on cancel/success. */
  verifyAttemptCount: number;
  /** The tx signature from the most recent signAndSend, used by the RECHECK handler. */
  lastTxSignature: string | null;
  /** Pre-computed token prices for all items (item.id → token amount). */
  itemPrices: Record<string, number>;
  /** The token used for pre-computed prices. */
  activeToken: WhitelistedToken | null;
}

const INITIAL_STATE: TokenShopState = {
  items: [],
  selectedItem: null,
  selectedToken: null,
  tokenPrice: null,
  purchaseState: "idle",
  purchaseError: null,
  lastPurchaseId: null,
  verifyAttemptCount: 0,
  lastTxSignature: null,
  itemPrices: {},
  activeToken: null,
};

export class TokenShopService {
  private state: TokenShopState = { ...INITIAL_STATE };
  private purchaseInFlight = false;

  /** Returns the current TokenShopState (immutable snapshot). */
  getState(): TokenShopState {
    return this.state;
  }

  /**
   * Increment the verify attempt counter during the verifying phase.
   * Capped at 5 to match the edge function retry limit.
   */
  incrementVerifyAttempt(): void {
    if (this.state.verifyAttemptCount < 5) {
      this.state = { ...this.state, verifyAttemptCount: this.state.verifyAttemptCount + 1 };
    }
  }

  /**
   * Load active shop items from Supabase.
   * Populates state.items.
   */
  async loadItems(): Promise<void> {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("token_shop_items")
        .select("id, item_type, item_id, display_name, usd_price, gem_amount, sort_order")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });

      if (error) {
        console.warn("[tokenShop] loadItems error:", error.message);
        return;
      }

      const rows = (data ?? []) as Array<{
        id: string;
        item_type: string;
        item_id: string;
        display_name: string;
        usd_price: number;
        gem_amount: number | null;
        sort_order: number;
      }>;

      this.state = {
        ...this.state,
        items: rows.map((row) => ({
          id: row.id,
          itemType: row.item_type as ShopItem["itemType"],
          itemId: row.item_id,
          displayName: row.display_name,
          usdPrice: Number(row.usd_price),
          gemAmount: row.gem_amount,
          sortOrder: row.sort_order,
        })),
      };
    } catch (err) {
      console.warn("[tokenShop] loadItems exception:", err instanceof Error ? err.message : String(err));
    }
  }

  /**
   * Load active whitelisted tokens from Supabase.
   * Used to populate payment token options in the UI.
   */
  async loadWhitelistedTokens(): Promise<WhitelistedToken[]> {
    try {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from("token_whitelist")
        .select("id, mint_address, symbol, decimals, usd_rate_feed, manual_usd_rate, is_active")
        .eq("is_active", true);

      if (error) {
        console.warn("[tokenShop] loadWhitelistedTokens error:", error.message);
        return [];
      }

      const rows = (data ?? []) as Array<{
        id: string;
        mint_address: string;
        symbol: string;
        decimals: number;
        usd_rate_feed: string | null;
        manual_usd_rate: number | null;
        is_active: boolean;
      }>;

      return rows.map((row) => ({
        id: row.id,
        mintAddress: row.mint_address,
        symbol: row.symbol,
        decimals: row.decimals,
        usdRateFeed: row.usd_rate_feed,
        manualUsdRate: row.manual_usd_rate != null ? Number(row.manual_usd_rate) : null,
        isActive: row.is_active,
      }));
    } catch (err) {
      console.warn("[tokenShop] loadWhitelistedTokens exception:", err instanceof Error ? err.message : String(err));
      return [];
    }
  }

  /**
   * Pre-compute token prices for all loaded items using the first active token.
   * Called after loadItems + loadWhitelistedTokens complete.
   */
  async precomputePrices(tokens: WhitelistedToken[]): Promise<void> {
    if (tokens.length === 0 || this.state.items.length === 0) return;
    const token = tokens[0];
    const prices: Record<string, number> = {};
    for (const item of this.state.items) {
      const price = await this.calculateTokenPrice(item, token);
      if (price != null) prices[item.id] = price;
    }
    this.state = { ...this.state, itemPrices: prices, activeToken: token };
    if (import.meta.env.DEV) console.log("[tokenShop] precomputed prices for", Object.keys(prices).length, "items using", token.symbol);
  }

  /**
   * Calculate the token amount required to purchase a shop item.
   * Uses Pyth price feed when available; falls back to manual USD rate.
   *
   * @returns Token amount rounded to token.decimals precision, or null if rate unavailable.
   */
  async calculateTokenPrice(item: ShopItem, token: WhitelistedToken): Promise<number | null> {
    let usdPerToken: number | null = null;

    // Use pricingService fallback chain (Moralis -> Jupiter v3 -> null) for live price
    usdPerToken = await fetchTokenUsdPrice(token.mintAddress);

    // Fall back to manual USD rate when pricingService returns null (both APIs down)
    if (usdPerToken == null && token.manualUsdRate != null && token.manualUsdRate > 0) {
      usdPerToken = token.manualUsdRate;
    }

    if (usdPerToken == null) {
      console.warn("[tokenShop] No price available for token:", token.symbol);
      return null;
    }

    const tokenAmount = item.usdPrice / usdPerToken;

    // Round to token decimal precision
    const factor = Math.pow(10, token.decimals);
    const rounded = Math.ceil(tokenAmount * factor) / factor;

    this.state = { ...this.state, tokenPrice: rounded };
    return rounded;
  }

  /**
   * Select an item for purchase and transition to 'confirming' state.
   * Fires calculateTokenPrice in the background to populate tokenPrice.
   */
  selectItem(item: ShopItem, token: WhitelistedToken): void {
    this.state = {
      ...this.state,
      selectedItem: item,
      selectedToken: token,
      tokenPrice: null, // will be populated by calculateTokenPrice
      purchaseState: "confirming",
      purchaseError: null,
    };

    // Fire price calculation in background — UI shows "..." until populated
    void this.calculateTokenPrice(item, token);
  }

  /**
   * Set success state (used by RECHECK handler when verify succeeds after manual retry).
   */
  setSuccess(purchaseId: string | null): void {
    this.state = { ...this.state, purchaseState: "success", lastPurchaseId: purchaseId, purchaseError: null };
  }

  /**
   * Set an error state (e.g. insufficient balance) without going through the full purchase flow.
   */
  setError(message: string): void {
    this.state = { ...this.state, purchaseState: "error", purchaseError: message };
  }

  /**
   * Cancel the current purchase and reset to idle.
   */
  cancelPurchase(): void {
    this.state = {
      ...this.state,
      selectedItem: null,
      selectedToken: null,
      tokenPrice: null,
      purchaseState: "idle",
      purchaseError: null,
      verifyAttemptCount: 0,
    };
  }

  /**
   * Execute the confirmed purchase flow:
   * 1. Get unsigned tx from token-purchase-intent Edge Function (server-side, team wallet hidden)
   * 2. Sign and send the transaction via wallet provider
   * 3. Submit tx signature to token-purchase-verify for on-chain verification and item crediting
   */
  async confirmPurchase(): Promise<{ success: boolean; error?: string }> {
    // Double-tap guard — prevent duplicate transactions while one is in flight
    if (this.purchaseInFlight) {
      return { success: false, error: "Purchase already in progress" };
    }

    // Cross-tab lock (#143) — prevent simultaneous purchases across browser tabs
    if (!acquirePurchaseLock()) {
      return { success: false, error: "Purchase in progress in another tab" };
    }

    this.purchaseInFlight = true;

    try {
      const { selectedItem, selectedToken } = this.state;
      if (!selectedItem || !selectedToken) {
        return { success: false, error: "No item or token selected" };
      }

      this.state = { ...this.state, purchaseState: "signing" };

      const walletAddress = solanaService.getState().address;
      if (!walletAddress) {
        const err = "Wallet address unavailable";
        this.state = { ...this.state, purchaseState: "error", purchaseError: err };
        return { success: false, error: err };
      }

      try {
        // Step 1: Request unsigned transaction from server
        const supabase = getSupabase();
        const { data: intentData, error: intentError } = await supabase.functions.invoke("token-purchase-intent", {
          body: {
            shopItemId: selectedItem.id,
            tokenMint: selectedToken.mintAddress,
            payerWallet: walletAddress,
          },
        });

        if (intentError || !intentData?.success) {
          const errMsg = intentError?.message ?? intentData?.error ?? "Failed to build transaction";
          console.warn("[tokenShop] token-purchase-intent error:", errMsg);
          this.state = { ...this.state, purchaseState: "error", purchaseError: errMsg };
          return { success: false, error: errMsg };
        }

        const { transaction: txBase64, purchaseId: intentPurchaseId } = intentData as {
          transaction: string;
          purchaseId?: string;
          tokenAmount: number;
          tokenAmountDisplay: string;
          tokenSymbol: string;
        };

        // Step 2: Deserialize and sign the transaction client-side
        const txBytes = Uint8Array.from(atob(txBase64), (c) => c.charCodeAt(0));
        const tx = Transaction.from(txBytes);

        let signature: string;

        try {
          signature = await solanaService.signAndSend(tx);
        } catch (signErr) {
          const errMsg = mapWalletError(signErr instanceof Error ? signErr.message : "Wallet signing failed");
          console.warn("[tokenShop] signAndSend failed:", errMsg);
          this.state = { ...this.state, purchaseState: "error", purchaseError: errMsg };
          return { success: false, error: errMsg };
        }

        // Step 3: Verify transaction on-chain
        this.state = { ...this.state, purchaseState: "verifying", verifyAttemptCount: 1, lastTxSignature: signature };

        const { data: verifyData, error: verifyError } = await supabase.functions.invoke("token-purchase-verify", {
          body: {
            txSignature: signature,
            shopItemId: selectedItem.id,
            tokenMint: selectedToken.mintAddress,
            purchaseId: intentPurchaseId,  // links verify to the intent record
          },
        });

        if (verifyError) {
          const errMsg = verifyError.message ?? "Verification failed";
          this.state = { ...this.state, purchaseState: "error", purchaseError: errMsg };
          return { success: false, error: errMsg };
        }

        const verifyResult = verifyData as { status: string; purchaseId?: string; error?: string };

        if (verifyResult.status === "verified") {
          this.state = {
            ...this.state,
            purchaseState: "success",
            lastPurchaseId: verifyResult.purchaseId ?? null,
            purchaseError: null,
          };
          // Force balance refresh after successful purchase (#150)
          this.refreshBalanceAfterPurchase();
          return { success: true };
        } else if (verifyResult.status === "pending_retry") {
          this.state = {
            ...this.state,
            purchaseState: "processing",
            purchaseError: null,
          };
          return { success: false, error: "Transaction is processing — please check back shortly" };
        } else {
          const errMsg = verifyResult.error ?? verifyResult.status ?? "Purchase verification failed";
          this.state = { ...this.state, purchaseState: "error", purchaseError: errMsg };
          return { success: false, error: errMsg };
        }
      } catch (err) {
        const errMsg = mapWalletError(err instanceof Error ? err.message : "Unexpected error during purchase");
        console.warn("[tokenShop] confirmPurchase exception:", errMsg);
        this.state = { ...this.state, purchaseState: "error", purchaseError: errMsg };
        return { success: false, error: errMsg };
      }
    } finally {
      this.purchaseInFlight = false;
      releasePurchaseLock();
    }
  }

  /**
   * Fire-and-forget balance refresh after a successful purchase (#150).
   * Ensures the UI reflects credited gems even if the purchase confirmation was delayed.
   */
  private refreshBalanceAfterPurchase(): void {
    void (async () => {
      try {
        const supabase = getSupabase();
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const { fetchBalance } = await import("../economy/walletManager");
        await fetchBalance(user.id);
      } catch (err) {
        console.warn("[tokenShop] post-purchase balance refresh failed:", err instanceof Error ? err.message : err);
      }
    })();
  }
}

export const tokenShopService = new TokenShopService();
